<script setup>
// SiteUp V2 — F1.5b: chegada de lead vindo de anúncio Click-to-WhatsApp.
//
// O backend já gravava tudo isto em conversation.custom_attributes desde o
// CtwaReferralProcessor — e NADA aparecia na tela. O atendente recebia
// "oi, vi o anúncio" sem saber QUAL anúncio.
//
// REDESENHADO 2026-08-10 (Lucas, ao ver em produção): o card mostrava o texto
// INTEIRO do anúncio e comia metade da tela, sem como recolher. Anúncio de
// clínica tem copy longa — o contexto virou obstáculo entre o atendente e a
// conversa. Agora:
//   1. nasce COMPACTO: uma linha com o título do anúncio;
//   2. expande sob demanda, e só aí mostra corpo, link e dados técnicos;
//   3. RECOLHE SOZINHO quando a pessoa começa a escrever — na hora de
//      responder, o contexto já foi lido e o espaço é da resposta.
import { computed, ref, onMounted, onBeforeUnmount } from 'vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import AdOriginDetails from './AdOriginDetails.vue';

const props = defineProps({
  conversation: { type: Object, default: null },
});

const expanded = ref(false);

const attrs = computed(() => props.conversation?.custom_attributes || {});

// Só existe quando veio de anúncio. Sem clid não houve referral processado.
const isFromAd = computed(() => Boolean(attrs.value.ctwa_clid));

const headline = computed(() => attrs.value.ctwa_headline || '');
const isPost = computed(() => attrs.value.ctwa_source_type === 'post');

const title = computed(() => (isPost.value ? 'TITLE_POST' : 'TITLE_AD'));

// Recolhe quando a pessoa COMEÇA A DIGITAR no composer.
//
// A primeira versão escutava `focusin` e falhava no caso mais comum: se o
// composer JÁ estava focado quando o card foi expandido, `focus()` não dispara
// evento nenhum e o card ficava aberto pra sempre (medido no staging).
// `keydown` + `input` cobrem digitação real, colar e ditado — e é literalmente
// o que foi pedido: "quando começar a digitar tinha que fechar".
//
// Escutar no documento e checar o ancestral mantém isto SELF-CONTAINED: sem
// prop nova, sem evento global, sem acoplar o card ao ReplyBox (outro módulo).
const collapseOnComposerActivity = event => {
  if (!expanded.value) return;
  if (event.target?.closest?.('.reply-box')) expanded.value = false;
};

const ACTIVITY_EVENTS = ['keydown', 'input', 'focusin'];

onMounted(() =>
  ACTIVITY_EVENTS.forEach(evt =>
    document.addEventListener(evt, collapseOnComposerActivity)
  )
);
onBeforeUnmount(() =>
  ACTIVITY_EVENTS.forEach(evt =>
    document.removeEventListener(evt, collapseOnComposerActivity)
  )
);
</script>

<template>
  <div v-if="isFromAd" class="flex justify-center px-4 pt-3">
    <div class="w-full max-w-md rounded-xl bg-n-alpha-2">
      <!-- Cabeçalho: é o card inteiro quando recolhido, e o gatilho sempre. -->
      <button
        type="button"
        data-testid="ad-arrival-toggle"
        class="flex w-full items-center gap-2.5 rounded-xl p-3 text-left transition-colors hover:bg-white/[0.03]"
        :aria-expanded="expanded"
        @click="expanded = !expanded"
      >
        <span
          class="flex justify-center items-center rounded-[var(--workspace-radius,10px)] size-7 shrink-0 bg-white/[0.06]"
        >
          <Icon class="size-3.5 text-n-blue-9" icon="i-ri-megaphone-line" />
        </span>
        <span class="flex flex-col flex-1 min-w-0">
          <span
            class="text-[13px] font-bold leading-tight tracking-tight font-display text-n-slate-12"
          >
            {{ $t(`CONVERSATION.AD_ARRIVAL.${title}`) }}
          </span>
          <!-- Recolhido: o título do anúncio numa linha só. É o suficiente
               pra saber de onde a pessoa veio sem ler a peça inteira. -->
          <span
            v-if="headline"
            class="mt-0.5 truncate text-xs leading-snug text-n-slate-11"
            :class="{ 'whitespace-normal': expanded }"
          >
            {{ headline }}
          </span>
        </span>
        <Icon
          class="size-4 shrink-0 text-n-slate-11 transition-transform duration-150"
          :class="{ 'rotate-180': expanded }"
          icon="i-ri-arrow-down-s-line"
        />
      </button>

      <AdOriginDetails v-if="expanded" :attrs="attrs" class="px-3 pb-3" />
    </div>
  </div>
</template>
